import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { MessageSquare, Send, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useNavigate } from 'react-router-dom';
import { lambdaService } from '../../../services/lambdaService';
import { useUpstreamFile } from '../useUpstreamFile';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

export const ChatNode: React.FC<{ id: string; data: { label: string; color: string; isTemplate?: boolean } }> = ({ id, data }) => {
  const navigate = useNavigate();
  const selectedFile = useUpstreamFile(id);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !selectedFile || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);
    try {
      const res = await lambdaService.sendChatMessage({
        message: userMsg,
        selectedFile: selectedFile.fileName,
        selectedFileKey: selectedFile.s3Key,
      });
      const reply = res.message || 'No response';
      setMessages((prev) => [...prev, { role: 'assistant', text: reply.slice(0, 200) }]);
    } catch (e: any) {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Error: ' + (e.message || 'Failed') }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[350px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <MessageSquare className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {!selectedFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile.fileName}</span>
          </div>
        )}

        {selectedFile && (
          <>
            {/* Messages */}
            <div
              ref={scrollRef}
              className="space-y-2 max-h-[180px] overflow-y-auto nodrag nowheel"
            >
              {messages.length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-3">
                  Ask a question about your data
                </p>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`text-[11px] rounded-lg px-2 py-1.5 ${
                    msg.role === 'user'
                      ? 'bg-primary/10 ml-4'
                      : 'bg-muted/50 mr-4'
                  }`}
                >
                  <span className="text-[9px] font-semibold text-muted-foreground">
                    {msg.role === 'user' ? 'You' : 'AI'}
                  </span>
                  <p className="mt-0.5 leading-relaxed">{msg.text}</p>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Thinking...
                </div>
              )}
            </div>

            {/* Input */}
            <div className="flex items-center gap-1 nodrag">
              <Input
                className="h-7 text-[11px] flex-1"
                placeholder="Ask a question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                disabled={loading}
              />
              <Button
                variant="secondary"
                size="sm"
                className="h-7 px-2"
                onClick={handleSend}
                disabled={loading || !input.trim()}
              >
                <Send className="h-3 w-3" />
              </Button>
            </div>

            <Button
              variant="link"
              size="sm"
              className="text-[11px] p-0 h-auto"
              onClick={() => navigate('/chat')}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open Full Chat
            </Button>
          </>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
